import * as THREE from "three";
import { mergeVertices } from "three/addons/utils/BufferGeometryUtils.js";

export interface IndexedMesh {
  positions: Float32Array;
  index: Uint32Array;
}

export interface MeshStats {
  volume: number;
  area: number;
  centroid: THREE.Vector3;
}

/** Signed distance function: negative inside. */
export type Sdf = (x: number, y: number, z: number) => number;

/** Unit icosphere with shared (welded) vertices and outward CCW winding. */
export function createIcosphere(detail: number): IndexedMesh {
  const source = new THREE.IcosahedronGeometry(1, detail);
  source.deleteAttribute("normal");
  source.deleteAttribute("uv");
  const merged = mergeVertices(source, 1e-5);
  const positions = new Float32Array(merged.getAttribute("position").array);
  const index = new Uint32Array(merged.getIndex()!.array);
  source.dispose();
  merged.dispose();
  return { positions, index };
}

/**
 * One pass over all triangles: area-weighted vertex normals (normalized),
 * per-vertex area (1/3 of adjacent triangles), enclosed volume and volume centroid.
 */
export function computeStats(
  pos: Float32Array,
  index: Uint32Array,
  outNormals: Float32Array,
  outAreas: Float32Array,
): MeshStats {
  outNormals.fill(0);
  outAreas.fill(0);
  let volume6 = 0;
  let area = 0;
  let mx = 0, my = 0, mz = 0;

  for (let t = 0; t < index.length; t += 3) {
    const ia = index[t] * 3, ib = index[t + 1] * 3, ic = index[t + 2] * 3;
    const ax = pos[ia], ay = pos[ia + 1], az = pos[ia + 2];
    const bx = pos[ib], by = pos[ib + 1], bz = pos[ib + 2];
    const cx = pos[ic], cy = pos[ic + 1], cz = pos[ic + 2];
    const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
    const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;

    outNormals[ia] += nx, outNormals[ia + 1] += ny, outNormals[ia + 2] += nz;
    outNormals[ib] += nx, outNormals[ib + 1] += ny, outNormals[ib + 2] += nz;
    outNormals[ic] += nx, outNormals[ic + 1] += ny, outNormals[ic + 2] += nz;

    const triArea = 0.5 * Math.sqrt(nx * nx + ny * ny + nz * nz);
    area += triArea;
    const third = triArea / 3;
    outAreas[ia / 3] += third;
    outAreas[ib / 3] += third;
    outAreas[ic / 3] += third;

    // Signed volume of the tetrahedron (origin, a, b, c), times 6.
    const v6 = ax * (by * cz - bz * cy) + ay * (bz * cx - bx * cz) + az * (bx * cy - by * cx);
    volume6 += v6;
    mx += v6 * (ax + bx + cx);
    my += v6 * (ay + by + cy);
    mz += v6 * (az + bz + cz);
  }

  for (let i = 0; i < outNormals.length; i += 3) {
    const x = outNormals[i], y = outNormals[i + 1], z = outNormals[i + 2];
    const inv = 1 / (Math.sqrt(x * x + y * y + z * z) || 1);
    outNormals[i] = x * inv;
    outNormals[i + 1] = y * inv;
    outNormals[i + 2] = z * inv;
  }

  const inv4 = volume6 !== 0 ? 1 / (4 * volume6) : 0;
  return {
    volume: volume6 / 6,
    area,
    centroid: new THREE.Vector3(mx * inv4, my * inv4, mz * inv4),
  };
}

/**
 * One-ring neighbours in CSR form. For a closed, consistently oriented mesh every
 * neighbour w of v appears exactly once as a directed edge v→w.
 */
export function buildAdjacency(index: Uint32Array, vertexCount: number) {
  const offsets = new Uint32Array(vertexCount + 1);
  for (let t = 0; t < index.length; t++) offsets[index[t] + 1]++;
  for (let v = 0; v < vertexCount; v++) offsets[v + 1] += offsets[v];
  const neighbors = new Uint32Array(offsets[vertexCount]);
  const fill = offsets.slice(0, vertexCount);
  for (let t = 0; t < index.length; t += 3) {
    const a = index[t], b = index[t + 1], c = index[t + 2];
    neighbors[fill[a]++] = b;
    neighbors[fill[b]++] = c;
    neighbors[fill[c]++] = a;
  }
  return { offsets, neighbors };
}

/** Scales positions about the origin so the enclosed volume equals `volume`. */
export function normalizeVolume(pos: Float32Array, index: Uint32Array, volume: number): void {
  const n = pos.length / 3;
  const stats = computeStats(pos, index, new Float32Array(n * 3), new Float32Array(n));
  const s = Math.cbrt(volume / stats.volume);
  for (let i = 0; i < pos.length; i++) pos[i] *= s;
}

/** Flips every triangle if the mesh is inside-out (encloses negative volume). */
export function orientOutward(mesh: IndexedMesh): void {
  const n = mesh.positions.length / 3;
  const { volume } = computeStats(
    mesh.positions,
    mesh.index,
    new Float32Array(n * 3),
    new Float32Array(n),
  );
  if (volume >= 0) return;
  const index = mesh.index;
  for (let t = 0; t < index.length; t += 3) {
    const b = index[t + 1];
    index[t + 1] = index[t + 2];
    index[t + 2] = b;
  }
}

// Inside tests cast rays along this direction, askew to the axes so they seldom graze the
// edges of a mesh; U and V complete it to an orthonormal basis.
const RAY_D = [0.5389, 0.6358, 0.5525];
const RAY_U = [0, 0.5525 / 0.8423, -0.6358 / 0.8423];
const RAY_V = [
  RAY_D[1] * RAY_U[2] - RAY_D[2] * RAY_U[1],
  RAY_D[2] * RAY_U[0] - RAY_D[0] * RAY_U[2],
  RAY_D[0] * RAY_U[1] - RAY_D[1] * RAY_U[0],
];

/**
 * Whether points are inside a closed mesh, by the parity of its surface crossings along a
 * ray. The triangles are binned by their shadow on a plane square to the ray, so a test
 * only looks at the few whose shadow covers the point's. Rebuild after the mesh moves.
 */
export class InsideTest {
  private u = new Float64Array(0);
  private v = new Float64Array(0);
  private d = new Float64Array(0);
  // The grid cell column and row of each vertex's shadow.
  private col = new Int32Array(0);
  private row = new Int32Array(0);
  private index = new Uint32Array(0);
  private start = new Uint32Array(0);
  private items = new Uint32Array(0);
  private u0 = 0;
  private v0 = 0;
  private cols = 0;
  private rows = 0;
  private inv = 1;

  /** Bins the mesh's triangles into square cells of size `cell`. */
  build(positions: Float32Array, index: Uint32Array, cell: number): void {
    const n = positions.length / 3;
    if (this.u.length < n) {
      this.u = new Float64Array(n);
      this.v = new Float64Array(n);
      this.d = new Float64Array(n);
      this.col = new Int32Array(n);
      this.row = new Int32Array(n);
    }
    const { u, v, d, col, row } = this;
    let u0 = Infinity, v0 = Infinity, u1 = -Infinity, v1 = -Infinity;
    for (let i = 0; i < n; i++) {
      const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
      const pu = u[i] = x * RAY_U[0] + y * RAY_U[1] + z * RAY_U[2];
      const pv = v[i] = x * RAY_V[0] + y * RAY_V[1] + z * RAY_V[2];
      d[i] = x * RAY_D[0] + y * RAY_D[1] + z * RAY_D[2];
      if (pu < u0) u0 = pu;
      if (pu > u1) u1 = pu;
      if (pv < v0) v0 = pv;
      if (pv > v1) v1 = pv;
    }
    const inv = 1 / cell;
    for (let i = 0; i < n; i++) {
      col[i] = Math.floor((u[i] - u0) * inv);
      row[i] = Math.floor((v[i] - v0) * inv);
    }
    const cols = Math.floor((u1 - u0) * inv) + 1, rows = Math.floor((v1 - v0) * inv) + 1;
    const cells = cols * rows;
    Object.assign(this, { index, u0, v0, cols, rows, inv });
    if (this.start.length < cells + 1) this.start = new Uint32Array(cells + 1);
    const start = this.start.fill(0, 0, cells + 1);
    // Counting sort of the triangles into every cell their shadow's bounding box touches.
    for (let t = 0; t < index.length; t += 3) {
      const a = index[t], b = index[t + 1], c = index[t + 2];
      const k0 = Math.min(col[a], col[b], col[c]), k1 = Math.max(col[a], col[b], col[c]);
      const r0 = Math.min(row[a], row[b], row[c]), r1 = Math.max(row[a], row[b], row[c]);
      for (let r = r0; r <= r1; r++) for (let k = k0; k <= k1; k++) start[r * cols + k + 1]++;
    }
    for (let c = 0; c < cells; c++) start[c + 1] += start[c];
    const total = start[cells];
    if (this.items.length < total) this.items = new Uint32Array(2 * total);
    const items = this.items, fill = start.slice(0, cells);
    for (let t = 0; t < index.length; t += 3) {
      const a = index[t], b = index[t + 1], c = index[t + 2];
      const k0 = Math.min(col[a], col[b], col[c]), k1 = Math.max(col[a], col[b], col[c]);
      const r0 = Math.min(row[a], row[b], row[c]), r1 = Math.max(row[a], row[b], row[c]);
      for (let r = r0; r <= r1; r++) for (let k = k0; k <= k1; k++) items[fill[r * cols + k]++] = t;
    }
  }

  contains(x: number, y: number, z: number): boolean {
    const { u, v, d, index, start, items } = this;
    const pu = x * RAY_U[0] + y * RAY_U[1] + z * RAY_U[2];
    const pv = x * RAY_V[0] + y * RAY_V[1] + z * RAY_V[2];
    const pd = x * RAY_D[0] + y * RAY_D[1] + z * RAY_D[2];
    const k = Math.floor((pu - this.u0) * this.inv), r = Math.floor((pv - this.v0) * this.inv);
    if (k < 0 || r < 0 || k >= this.cols || r >= this.rows) return false;
    const cell = r * this.cols + k;
    let odd = false;
    for (let e = start[cell]; e < start[cell + 1]; e++) {
      const t = items[e];
      const a = index[t], b = index[t + 1], c = index[t + 2];
      const au = u[a] - pu, av = v[a] - pv, bu = u[b] - pu, bv = v[b] - pv;
      const cu = u[c] - pu, cv = v[c] - pv;
      // Signed areas of the shadow's sub-triangles: all one sign when the point is inside.
      const wa = bu * cv - bv * cu, wb = cu * av - cv * au, wc = au * bv - av * bu;
      if ((wa < 0 || wb < 0 || wc < 0) && (wa > 0 || wb > 0 || wc > 0)) continue;
      const area = wa + wb + wc;
      if (area === 0) continue;
      // The ray crosses the triangle ahead of the point.
      if ((wa * d[a] + wb * d[b] + wc * d[c]) / area > pd) odd = !odd;
    }
    return odd;
  }
}

const CORNERS = [
  [0, 0, 0],
  [1, 0, 0],
  [0, 1, 0],
  [1, 1, 0],
  [0, 0, 1],
  [1, 0, 1],
  [0, 1, 1],
  [1, 1, 1],
];
const CUBE_EDGES = [
  [0, 1],
  [2, 3],
  [4, 5],
  [6, 7],
  [0, 2],
  [1, 3],
  [4, 6],
  [5, 7],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
];

/** Signed distances sampled at the points -bound + i·cell (i < n) of a cubic grid. */
export interface SdfGrid {
  n: number;
  bound: number;
  cell: number;
  /** Indexed (k·n + j)·n + i for the point (i, j, k). */
  values: Float32Array;
}

export function gridSize(bound: number, cell: number): number {
  return Math.ceil((2 * bound) / cell) + 1;
}

/** Grid points along each side of a block sampleSdf may fill at once. */
const SAMPLE_BLOCK = 8;

/**
 * Samples `sdf` over the grid in blocks. Away from the surface only the sign counts (see
 * meshGrid), so a block whose middle lies farther from the surface than its corners do,
 * with two cells to spare, takes the middle's distance throughout. Only blocks near the
 * surface are sampled point by point. This needs an SDF that does not overstate the distance.
 */
export function sampleSdf(sdf: Sdf, bound: number, cell: number): SdfGrid {
  const n = gridSize(bound, cell);
  const at = (i: number) => -bound + i * cell;
  const values = new Float32Array(n * n * n);
  const reach = (Math.sqrt(3) * (SAMPLE_BLOCK - 1) / 2 + 2) * cell;
  for (let k0 = 0; k0 < n; k0 += SAMPLE_BLOCK) {
    const k1 = Math.min(n, k0 + SAMPLE_BLOCK);
    for (let j0 = 0; j0 < n; j0 += SAMPLE_BLOCK) {
      const j1 = Math.min(n, j0 + SAMPLE_BLOCK);
      for (let i0 = 0; i0 < n; i0 += SAMPLE_BLOCK) {
        const i1 = Math.min(n, i0 + SAMPLE_BLOCK);
        const mid = (a: number, b: number) => at((a + b - 1) / 2);
        const d = sdf(mid(i0, i1), mid(j0, j1), mid(k0, k1));
        const far = Math.abs(d) > reach;
        for (let k = k0; k < k1; k++) {
          for (let j = j0; j < j1; j++) {
            for (let i = i0; i < i1; i++) {
              values[(k * n + j) * n + i] = far ? d : sdf(at(i), at(j), at(k));
            }
          }
        }
      }
    }
  }
  return { n, bound, cell, values };
}

/** Meshes the zero level of an SDF over the cube [-bound, bound]³ (see meshGrid). */
export function meshSdf(sdf: Sdf, bound: number, cell: number): IndexedMesh {
  return meshGrid(sampleSdf(sdf, bound, cell));
}

/**
 * Naive surface nets: one vertex per grid cell the surface passes through (the mean of
 * its edge crossings) and one quad per crossed grid edge. Works for any topology; the
 * surface must lie strictly inside the grid.
 */
export function meshGrid({ n, bound, cell, values }: SdfGrid): IndexedMesh {
  const at = (i: number) => -bound + i * cell;
  const value = (i: number, j: number, k: number) => values[(k * n + j) * n + i];

  const m = n - 1;
  const cellVertex = new Int32Array(m * m * m).fill(-1);
  const positions: number[] = [];
  const v = new Float32Array(8);
  for (let k = 0; k < m; k++) {
    for (let j = 0; j < m; j++) {
      for (let i = 0; i < m; i++) {
        let inside = 0;
        for (let c = 0; c < 8; c++) {
          v[c] = value(i + CORNERS[c][0], j + CORNERS[c][1], k + CORNERS[c][2]);
          if (v[c] < 0) inside++;
        }
        if (inside === 0 || inside === 8) continue;
        let sx = 0, sy = 0, sz = 0, count = 0;
        for (const [a, b] of CUBE_EDGES) {
          if ((v[a] < 0) === (v[b] < 0)) continue;
          const t = v[a] / (v[a] - v[b]);
          sx += CORNERS[a][0] + t * (CORNERS[b][0] - CORNERS[a][0]);
          sy += CORNERS[a][1] + t * (CORNERS[b][1] - CORNERS[a][1]);
          sz += CORNERS[a][2] + t * (CORNERS[b][2] - CORNERS[a][2]);
          count++;
        }
        cellVertex[(k * m + j) * m + i] = positions.length / 3;
        positions.push(at(i + sx / count), at(j + sy / count), at(k + sz / count));
      }
    }
  }

  // The four cells around a crossed edge, walked right-handedly about the edge's axis;
  // reversed when the edge leaves the inside, so every quad faces outward.
  const index: number[] = [];
  const cv = (i: number, j: number, k: number) => cellVertex[(k * m + j) * m + i];
  const quad = (a: number, b: number, c: number, d: number, reverse: boolean) => {
    if (reverse) index.push(a, c, b, a, d, c);
    else index.push(a, b, c, a, c, d);
  };
  for (let k = 1; k < m; k++) {
    for (let j = 1; j < m; j++) {
      for (let i = 1; i < m; i++) {
        const inside = value(i, j, k) < 0;
        if (inside !== (value(i + 1, j, k) < 0)) {
          quad(cv(i, j - 1, k - 1), cv(i, j, k - 1), cv(i, j, k), cv(i, j - 1, k), inside);
        }
        if (inside !== (value(i, j + 1, k) < 0)) {
          quad(cv(i - 1, j, k - 1), cv(i - 1, j, k), cv(i, j, k), cv(i, j, k - 1), inside);
        }
        if (inside !== (value(i, j, k + 1) < 0)) {
          quad(cv(i - 1, j - 1, k), cv(i, j - 1, k), cv(i, j, k), cv(i - 1, j, k), inside);
        }
      }
    }
  }

  const mesh = { positions: new Float32Array(positions), index: new Uint32Array(index) };
  orientOutward(mesh);
  return mesh;
}
